import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { LifeBuoy, Send, CheckCircle2, Hash } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  open: 'bg-red-100 text-red-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
};

export const CrmSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState('tickets');
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [channelMsgs, setChannelMsgs] = useState([]);
  const [activeChannel, setActiveChannel] = useState('general');

  useEffect(() => { fetchTickets(); }, [statusFilter]);
  useEffect(() => { if (activeTab === 'channels') fetchChannel(activeChannel); }, [activeTab, activeChannel]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API_URL}/api/admin/support/tickets${q}`, { withCredentials: true });
      setTickets(res.data);
    } catch (e) { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  };

  const fetchChannel = async (ch) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/chat/channel/${ch}`, { withCredentials: true });
      setChannelMsgs(res.data);
    } catch (e) { toast.error('Failed to load channel'); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return;
    try {
      await axios.post(`${API_URL}/api/admin/support/tickets/${selected.ticket_id}/reply`,
        { message: replyText }, { withCredentials: true });
      toast.success('Reply sent');
      setReplyText('');
      const updated = await axios.get(`${API_URL}/api/admin/support/tickets`, { withCredentials: true });
      setTickets(updated.data);
      const fresh = updated.data.find(t => t.ticket_id === selected.ticket_id);
      setSelected(fresh);
    } catch (e) { toast.error('Failed'); }
  };

  const resolve = async (ticketId) => {
    try {
      await axios.post(`${API_URL}/api/admin/support/tickets/${ticketId}/resolve`, {}, { withCredentials: true });
      toast.success('Ticket resolved');
      fetchTickets();
      if (selected?.ticket_id === ticketId) setSelected(null);
    } catch (e) { toast.error('Failed'); }
  };

  const assignToMe = async (ticketId) => {
    try {
      await axios.post(`${API_URL}/api/admin/support/tickets/${ticketId}/assign`, {}, { withCredentials: true });
      toast.success('Assigned to you');
      fetchTickets();
    } catch (e) { toast.error('Failed'); }
  };

  if (loading && activeTab === 'tickets') return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-[#1E1E24] flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          <LifeBuoy className="w-7 h-7 text-[#FF3B6B]" /> Support
        </h1>
        <p className="text-[#6B6B7A]">{tickets.length} tickets · {tickets.filter(t => t.status === 'open').length} open</p>
      </div>

      <div className="flex gap-2 mb-6">
        <Button onClick={() => setActiveTab('tickets')} className={`rounded-full ${activeTab === 'tickets' ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`} data-testid="tab-tickets">
          <LifeBuoy className="w-4 h-4 mr-1" /> Tickets
        </Button>
        <Button onClick={() => setActiveTab('channels')} className={`rounded-full ${activeTab === 'channels' ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`} data-testid="tab-channels">
          <Hash className="w-4 h-4 mr-1" /> Channels (Compliance)
        </Button>
      </div>

      {activeTab === 'tickets' && (
        <>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 mb-4 flex gap-3 items-end">
            <div>
              <label className="text-xs text-[#6B6B7A]">Filter by status</label>
              <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-44 mt-1 rounded-xl border-[#E8E5DF]" data-testid="ticket-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Initial Message</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#6B6B7A]">No tickets</TableCell></TableRow>
                ) : tickets.map(t => (
                  <TableRow key={t.ticket_id} data-testid={`ticket-${t.ticket_id}`}>
                    <TableCell className="text-xs">{new Date(t.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{t.customer_name || '—'}</p>
                      <p className="text-xs text-[#6B6B7A]">{t.customer_email}</p>
                    </TableCell>
                    <TableCell className="text-xs max-w-[260px] truncate">{t.initial_message}</TableCell>
                    <TableCell className="text-xs font-mono">{t.booking_id || '—'}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[t.status] || ''}`}>
                        {(t.status || '').replace('_', ' ')}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{t.assigned_to_name || '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setSelected(t)} className="h-7 text-xs rounded-full" data-testid={`view-ticket-${t.ticket_id}`}>View</Button>
                        {t.status !== 'resolved' && !t.assigned_to && (
                          <Button size="sm" onClick={() => assignToMe(t.ticket_id)} className="h-7 text-xs bg-[#06D6A0] text-white rounded-full" data-testid={`assign-ticket-${t.ticket_id}`}>Assign me</Button>
                        )}
                        {t.status !== 'resolved' && (
                          <Button size="sm" variant="ghost" onClick={() => resolve(t.ticket_id)} className="h-7 w-7 p-0 text-[#06D6A0]" title="Mark resolved" data-testid={`resolve-ticket-${t.ticket_id}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {activeTab === 'channels' && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
          <div className="flex border-b border-[#E8E5DF]">
            {['general', 'party-hosts', 'announcements'].map(ch => (
              <button
                key={ch}
                onClick={() => setActiveChannel(ch)}
                className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeChannel === ch ? 'border-[#FF3B6B] text-[#FF3B6B]' : 'border-transparent text-[#6B6B7A]'}`}
                data-testid={`channel-tab-${ch}`}
              >
                #{ch}
              </button>
            ))}
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-4 space-y-2">
            {channelMsgs.length === 0 ? (
              <p className="text-center text-sm text-[#6B6B7A] py-8">No messages yet in #{activeChannel}</p>
            ) : channelMsgs.slice().reverse().map(m => (
              <div key={m.message_id} className="flex gap-3 py-2 border-b border-[#E8E5DF]/50 last:border-0">
                <div className="w-8 h-8 rounded-full bg-[#FF3B6B]/10 flex items-center justify-center text-xs font-bold text-[#FF3B6B] flex-shrink-0">
                  {(m.sender_name || 'U').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs">
                    <span className="font-semibold">{m.sender_name}</span>
                    <span className="text-[#6B6B7A] ml-2 text-[10px]">{new Date(m.created_at).toLocaleString()}</span>
                  </p>
                  <p className="text-sm mt-0.5 whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>{m.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={v => !v && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Support Ticket</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3 text-sm">
                <p><strong>{selected.customer_name || 'Anonymous'}</strong> · <a href={`mailto:${selected.customer_email}`} className="text-[#FF3B6B]">{selected.customer_email}</a></p>
                {selected.booking_id && <p className="text-xs text-[#6B6B7A]">Booking: {selected.booking_id}</p>}
                <p className="text-xs text-[#6B6B7A]">Created: {new Date(selected.created_at).toLocaleString()}</p>
              </div>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {selected.messages.map((m, i) => (
                  <div key={i} className={`p-2 rounded-xl text-sm ${m.sender_type === 'admin' ? 'bg-[#FF3B6B]/10 ml-8' : 'bg-white border border-[#E8E5DF] mr-8'}`}>
                    <p className="text-[10px] font-semibold mb-0.5">{m.sender_name}</p>
                    <p className="whitespace-pre-wrap">{m.message}</p>
                    <p className="text-[9px] text-[#6B6B7A] mt-0.5">{new Date(m.at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              {selected.status !== 'resolved' && (
                <>
                  <Textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    rows={3}
                    placeholder="Type your reply to the customer…"
                    className="rounded-xl border-[#E8E5DF]"
                    data-testid="ticket-reply-input"
                  />
                  <div className="flex gap-2">
                    <Button onClick={sendReply} disabled={!replyText.trim()} className="flex-1 bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="send-ticket-reply">
                      <Send className="w-4 h-4 mr-1" /> Send Reply
                    </Button>
                    <Button variant="outline" onClick={() => resolve(selected.ticket_id)} className="rounded-full border-[#06D6A0] text-[#06D6A0]" data-testid="resolve-in-dialog">
                      Resolve
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmSupport;
